import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  EmailStatus,
  EmailPriority,
  EmailIntent,
  ContactFormStatus,
  ContactFormCategory,
} from '../../services/emailHubService';

export interface EmailFiltersValue {
  status?: EmailStatus | ContactFormStatus | '';
  priority?: EmailPriority | '';
  intent?: EmailIntent | '';
  category?: ContactFormCategory | '';
  mailbox?: string;
  search?: string;
}

interface EmailFiltersProps {
  filters: EmailFiltersValue;
  onChange: (filters: EmailFiltersValue) => void;
  activeTab: 'emails' | 'contact_forms';
}

const EMAIL_STATUS_OPTIONS: Array<{ value: EmailStatus | ''; labelKey: string }> = [
  { value: '', labelKey: 'filters.allStatuses' },
  { value: 'new', labelKey: 'status.new' },
  { value: 'read', labelKey: 'status.read' },
  { value: 'replied', labelKey: 'status.replied' },
  { value: 'auto_replied', labelKey: 'status.autoReplied' },
  { value: 'archived', labelKey: 'status.archived' },
  { value: 'spam', labelKey: 'status.spam' },
];

const CF_STATUS_OPTIONS: Array<{ value: ContactFormStatus | ''; labelKey: string }> = [
  { value: '', labelKey: 'filters.allStatuses' },
  { value: 'new', labelKey: 'status.new' },
  { value: 'read', labelKey: 'status.read' },
  { value: 'replied', labelKey: 'status.replied' },
  { value: 'archived', labelKey: 'status.archived' },
];

const PRIORITY_OPTIONS: Array<{ value: EmailPriority | ''; labelKey: string }> = [
  { value: '', labelKey: 'filters.allPriorities' },
  { value: 'urgent', labelKey: 'priority.urgent' },
  { value: 'high', labelKey: 'priority.high' },
  { value: 'normal', labelKey: 'priority.normal' },
  { value: 'low', labelKey: 'priority.low' },
];

const INTENT_OPTIONS: Array<{ value: EmailIntent | ''; labelKey: string }> = [
  { value: '', labelKey: 'filters.allIntents' },
  { value: 'account_help', labelKey: 'intent.account_help' },
  { value: 'billing_inquiry', labelKey: 'intent.billing_inquiry' },
  { value: 'bug_report', labelKey: 'intent.bug_report' },
  { value: 'feature_request', labelKey: 'intent.feature_request' },
  { value: 'creator_inquiry', labelKey: 'intent.creator_inquiry' },
  { value: 'partnership', labelKey: 'intent.partnership' },
  { value: 'content_feedback', labelKey: 'intent.content_feedback' },
  { value: 'general_question', labelKey: 'intent.general_question' },
  { value: 'spam', labelKey: 'intent.spam' },
  { value: 'other', labelKey: 'intent.other' },
];

const MAILBOX_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All Mailboxes' },
  { value: 'support@loopyai.app', label: 'support@loopyai.app' },
  { value: 'official@loopyai.app', label: 'official@loopyai.app' },
  { value: 'hello@loopyai.app', label: 'hello@loopyai.app' },
  { value: 'info@loopyai.app', label: 'info@loopyai.app' },
  { value: 'weekly@loopyai.app', label: 'weekly@loopyai.app' },
];

const CATEGORY_OPTIONS: Array<{ value: ContactFormCategory | ''; labelKey: string }> = [
  { value: '', labelKey: 'filters.allCategories' },
  { value: 'general', labelKey: 'contact.categoryGeneral' },
  { value: 'partnership', labelKey: 'contact.categoryPartnership' },
  { value: 'creator_inquiry', labelKey: 'contact.categoryCreator' },
  { value: 'bug_report', labelKey: 'contact.categoryBug' },
  { value: 'feedback', labelKey: 'contact.categoryFeedback' },
  { value: 'billing', labelKey: 'contact.categoryBilling' },
  { value: 'press', labelKey: 'contact.categoryPress' },
];

const SEARCH_DEBOUNCE_MS = 300;

const EmailFilters: React.FC<EmailFiltersProps> = ({ filters, onChange, activeTab }) => {
  const { t } = useTranslation('emailHub');
  const [localSearch, setLocalSearch] = useState(filters.search ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local search when filters.search is reset externally (e.g. tab change)
  useEffect(() => {
    setLocalSearch(filters.search ?? '');
  }, [filters.search]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setLocalSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange({ ...filters, search: value });
    }, SEARCH_DEBOUNCE_MS);
  }, [filters, onChange]);

  const handleClearSearch = useCallback(() => {
    setLocalSearch('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onChange({ ...filters, search: '' });
  }, [filters, onChange]);

  const handleChange = (key: keyof EmailFiltersValue, value: string) => {
    onChange({ ...filters, [key]: value });
  };

  const statusOptions = activeTab === 'emails' ? EMAIL_STATUS_OPTIONS : CF_STATUS_OPTIONS;

  return (
    <div className="email-hub-filters">
      {/* Search */}
      <div className="email-hub-filters__search-wrap">
        <svg className="email-hub-filters__search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input
          type="text"
          className="email-hub-filters__search"
          placeholder={t('filters.searchPlaceholder', 'Search by sender, subject...')}
          value={localSearch}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        {localSearch && (
          <button
            className="email-hub-filters__clear-search"
            onClick={handleClearSearch}
            aria-label={t('filters.clearSearch', 'Clear search')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Status */}
      <select
        className="email-hub-filters__select"
        value={filters.status ?? ''}
        onChange={(e) => handleChange('status', e.target.value)}
        aria-label={t('filters.status', 'Status')}
      >
        {statusOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {t(opt.labelKey, opt.labelKey)}
          </option>
        ))}
      </select>

      {/* Email-specific filters */}
      {activeTab === 'emails' && (
        <>
          <select
            className="email-hub-filters__select"
            value={filters.mailbox ?? ''}
            onChange={(e) => handleChange('mailbox', e.target.value)}
            aria-label={t('filters.mailbox', 'Mailbox')}
          >
            {MAILBOX_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.value === '' ? t('filters.allMailboxes', 'All Mailboxes') : opt.label}
              </option>
            ))}
          </select>

          <select
            className="email-hub-filters__select"
            value={filters.priority ?? ''}
            onChange={(e) => handleChange('priority', e.target.value)}
            aria-label={t('filters.priority', 'Priority')}
          >
            {PRIORITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(opt.labelKey, opt.labelKey)}
              </option>
            ))}
          </select>

          <select
            className="email-hub-filters__select"
            value={filters.intent ?? ''}
            onChange={(e) => handleChange('intent', e.target.value)}
            aria-label={t('filters.intent', 'Intent')}
          >
            {INTENT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(opt.labelKey, opt.labelKey)}
              </option>
            ))}
          </select>
        </>
      )}

      {/* Contact form-specific filters */}
      {activeTab === 'contact_forms' && (
        <select
          className="email-hub-filters__select"
          value={filters.category ?? ''}
          onChange={(e) => handleChange('category', e.target.value)}
          aria-label={t('filters.category', 'Category')}
        >
          {CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.labelKey, opt.labelKey)}
            </option>
          ))}
        </select>
      )}
    </div>
  );
};

export default EmailFilters;
